import React, { useContext, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AxiosError } from "axios";
import { QueryResult, QueryKey, queryCache, useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { ApiError } from "fe-shared/src/types/common";

import UserContext from "contexts/userContext";
import TransactionStatusInfo from "components/TransactionStatus";
import TransactionContentView from "components/TransactionContentView";
import { CloseIcon, ReturnIcon } from "fe-shared/src/components/Icons";
import {
  Button,
  Dialog,
  LinkButton,
  Loader,
  Banner,
  CollapseComponent,
  NotFoundPage,
  RecordsSidebar,
  showNotification,
  TextInput,
} from "fe-shared/src/components";
import {
  DialogHeader,
  EditDispositionDialog,
  SectionEditAction,
  InvoiceLink,
  CreateReceiptDialog,
  ProcessingStatusBanner,
} from "fe-shared-transactions/src/components";
import ClaimNumberLabel from "fe-shared-transactions/src/components/ClaimNumberLabel";
import TrailerNumberLabel from "fe-shared-transactions/src/components/TrailerNumberLabel";
import ClaimNumberEditButton from "fe-shared-transactions/src/components/ClaimNumberEditButton";
import TrailerNumberEditButton from "fe-shared-transactions/src/components/TrailerNumberEditButton";

import useDialog from "fe-shared/src/hooks/useDialog";

import {
  BusinessRole,
  Company,
  PermissionLevel,
  UserPermission,
} from "fe-shared/src/types/domain";
import {
  hasAnyPermission,
  hasPermission,
} from "fe-shared/src/helpers/permissions";
import {
  Transaction,
  CorrectionRawResponse,
  TransactionType,
  TransactionStatus,
  CreateReceiptFormValues,
} from "fe-shared-transactions/src/types";
import correctionResponseMapper from "fe-shared-transactions/src/helpers/correctionResponseMapper";
import { DISPOSITION_EDIT_BUTTON_PORTAL_ID } from "fe-shared-transactions/src/consts";
import {
  CORRECTIONS_QUERY_KEY,
  PERMISSIONS,
  TRANSACTIONS_QUERY_KEY,
  TRANSACTION_ACTIVITIES_QUERY_KEY,
} from "consts";

import * as api from "./modules/api";
import styles from "./TransactionDetails.module.scss";

// be-transaction-synchronization-service error codes for the cancel-delivery
// endpoint (its ExceptionCodeEnum collides with be-transaction-service's, so
// these cannot be resolved via the shared `errors.<code>` toast dictionary).
const CANCEL_DELIVERY_ERROR_KEYS: Record<number, string> = {
  1108: "transactions.actions.cancelErrors.1108",
  1109: "transactions.actions.cancelErrors.1109",
};

const getCancelDeliveryErrorKey = (errorCode?: number): string =>
  (errorCode && CANCEL_DELIVERY_ERROR_KEYS[errorCode]) ||
  "transactions.actions.cancelError";

interface Props {
  company: Company;
  returnUrl: string;
  transaction: QueryResult<Transaction, AxiosError<ApiError>>;
  linkedAdjustment: QueryResult<Transaction, AxiosError<ApiError>>;
  corrections: QueryResult<CorrectionRawResponse, AxiosError<ApiError>>;
}

const FORWARD_TRACE_TYPE_AVAILABLE_LIST = [
  TransactionType.Shipment,
  TransactionType.Pickup,
  TransactionType.Delivery,
  TransactionType.Receipt,
];

const BACKWARD_TRACE_TYPE_AVAILABLE_LIST = [
  TransactionType.Shipment,
  TransactionType.Pickup,
  TransactionType.Delivery,
  TransactionType.Receipt,
  TransactionType.Disposition,
  TransactionType.Adjustment,
];

const TransactionDetailsView = (props: Props) => {
  const {
    company,
    returnUrl,
    transaction,
    corrections,
    linkedAdjustment,
  } = props;
  const {
    data,
    isLoading: isLoadingTransaction,
    isFetching: isFetchingTransaction,
    isError: isErrorTransaction,
  } = transaction;
  const {
    data: linkedAdjustmentData,
    isLoading: isLoadingLinkedAdjustment,
    isFetching: isFetchingLinkedAdjustment,
  } = linkedAdjustment;
  const {
    data: correctionsData,
    isLoading: isLoadingCorrections,
    isFetching: isFetchingCorrections,
    isError: isErrorCorrections,
  } = corrections;
  const { t } = useTranslation();
  const history = useHistory();
  const currentUser = useContext(UserContext);
  const [
    editDispositionContainer,
    setEditDispositionContainer,
  ] = useState<HTMLDivElement | null>(null);
  const {
    isDialogActive,
    isDialogRendered,
    openDialog,
    closeDialog,
  } = useDialog();

  const {
    isDialogActive: isCancelDialogActive,
    isDialogRendered: isCancelDialogRendered,
    openDialog: openCancelDialog,
    closeDialog: closeCancelDialog,
  } = useDialog();

  const [cancelReason, setCancelReason] = useState("");

  const {
    isDialogActive: isCreateReceiptDialogActive,
    isDialogRendered: isCreateReceiptDialogRendered,
    openDialog: openCreateReceiptDialog,
    closeDialog: closeCreateReceiptDialog,
  } = useDialog();

  const canViewHistory = hasAnyPermission(company?.permissions, [
    {
      name: PERMISSIONS.transactionHistory,
      level: PermissionLevel.Read,
    },
  ]);

  const isPermitted = (permission: UserPermission) =>
    hasPermission(company?.permissions, permission);

  const canExportHistory = isPermitted({
    name: PERMISSIONS.transactionHistoryExport,
    level: PermissionLevel.Full,
  });

  const canEditDisposition = isPermitted({
    name: PERMISSIONS.editDisposition,
    level: PermissionLevel.Full,
  });
  const canUpdateTrailerNumber = isPermitted({
    name: PERMISSIONS.transactionUpdateTrailerNumber,
    level: PermissionLevel.Full,
  });
  const canUpdateClaimNumber = isPermitted({
    name: PERMISSIONS.transactionUpdateClaimNumber,
    level: PermissionLevel.Full,
  });

  const isProcessor = company?.businessRoleCode === BusinessRole.Processor;
  const isHauler = company?.businessRoleCode === BusinessRole.Hauler;

  // check sits on top of the permission rather than replacing it.
  const canSeeLinkedBatches =
    isHauler &&
    isPermitted({
      name: PERMISSIONS.transactionSeeTraceability,
      level: PermissionLevel.Read,
    });

  const hasSidebar = canViewHistory || canSeeLinkedBatches;

  const isInitiatedDelivery =
    data?.transactionType === TransactionType.Delivery &&
    data?.statusCode === TransactionStatus.Synchronizing &&
    data?.processingStatusCode !== "InProgress";

  const canCreateReceipt =
    isInitiatedDelivery &&
    isProcessor &&
    isPermitted({
      name: PERMISSIONS.transactionCreateReceipt,
      level: PermissionLevel.Full,
    });

  const [applyCreateReceipt, createReceiptResult] = useMutation<
    void,
    AxiosError<ApiError>,
    CreateReceiptFormValues
  >(
    (values) => api.createReceipt(data!.transactionId, values),
    {
      onSuccess: () => {
        // Prefix-invalidate so the grid list query (keyed by requestParams, not
        // transactionNumber) refetches — the receipted delivery drops off the
        // processor's grid — then return to the grid.
        queryCache.invalidateQueries([TRANSACTIONS_QUERY_KEY]);
        showNotification(t("transactions.actions.createReceiptSuccess"));
        history.push(returnUrl);
      },
      onError: () => {
        showNotification(
          t("transactions.actions.createReceiptError"),
          undefined,
          CloseIcon
        );
      },
    }
  );

  const { isLoading: isCreateReceiptLoading } = createReceiptResult;

  const canCancel =
    isInitiatedDelivery &&
    isPermitted({
      name: PERMISSIONS.transactionInvalidation,
      level: PermissionLevel.Full,
    });

  const [applyInvalidate, invalidateResult] = useMutation<
    void,
    AxiosError<ApiError>
  >(() => api.cancelDeliveryTransaction(data!.transactionNumber, cancelReason), {
    onSuccess: () => {
      // Same as create receipt: prefix-invalidate to refetch the grid (the
      // cancelled delivery is now Invalid and filtered out), then return.
      queryCache.invalidateQueries([TRANSACTIONS_QUERY_KEY]);
      setCancelReason("");
      showNotification(t("transactions.actions.cancelSuccess"));
      history.push(returnUrl);
    },
    onError: (error: AxiosError<ApiError>) => {
      const errorKey = getCancelDeliveryErrorKey(error.response?.data?.errorCode);

      showNotification(t(errorKey), undefined, CloseIcon);
    },
  });

  const { isLoading: isInvalidateLoading } = invalidateResult;

  const isTransactionNotFound = transaction.error?.response?.status === 404;

  const mappedCorrections = useMemo(() => {
    if (correctionsData && transaction.data) {
      const isCorrectionsExist = !!Object.values(correctionsData).filter(
        Boolean
      ).length;

      return isCorrectionsExist
        ? correctionResponseMapper(correctionsData, transaction.data)
        : null;
    }

    return null;
  }, [correctionsData, transaction.data]);

  useLayoutEffect(() => {
    const element = document.getElementById(
      DISPOSITION_EDIT_BUTTON_PORTAL_ID
    ) as HTMLDivElement;

    if (element) {
      setEditDispositionContainer(element);
    }
  }, [data]);

  const renderContent = () => {
    const {
      transactionType,
      transactionNumber,
      statusCode,
      isAdjustable,
    } = data!;
    const activitiesQueryKey: QueryKey = [
      TRANSACTION_ACTIVITIES_QUERY_KEY,
      transactionNumber,
    ];
    const transactionDetailsQueryKey: QueryKey = [
      TRANSACTIONS_QUERY_KEY,
      transactionNumber,
    ];
    const correctionsQueryKey: QueryKey = [
      CORRECTIONS_QUERY_KEY,
      transactionNumber,
    ];
    const forwardTraceabilityQueryKey: QueryKey = [
      TRANSACTION_ACTIVITIES_QUERY_KEY,
      transactionNumber,
      "forward",
    ];
    const backwardTraceabilityQueryKey: QueryKey = [
      TRANSACTION_ACTIVITIES_QUERY_KEY,
      transactionNumber,
      "backward",
    ];
    const isInCorrectionPeriod =
      statusCode === TransactionStatus.Submitted && isAdjustable;

    const isClaimNumberVisible =
      transactionType === TransactionType.Delivery ||
      transactionType === TransactionType.Receipt;

    const isTrailerNumberVisible =
      transactionType === TransactionType.Pickup ||
      transactionType === TransactionType.Shipment ||
      transactionType === TransactionType.Delivery ||
      transactionType === TransactionType.Receipt;

    const isDispositionEditVisible =
      canEditDisposition &&
      transactionType === TransactionType.Disposition &&
      isInCorrectionPeriod;

    const getDialogTitle = (title: string) => (
      <DialogHeader
        title={title}
        type={transactionType}
        number={transactionNumber}
        translationRoot="transactions"
      />
    );

    return (
      <>
        <main className={styles.reqDetailsContent}>
          <div className={styles.reqDetailsContentWrapper}>
            <div className={styles.reqDetailsNavigation}>
              <LinkButton
                to={returnUrl}
                variant="inline"
                id="request-back-link"
                iconComponent={ReturnIcon}
                className={styles.reqDetailsBackLink}
              >
                {t("transactions.grid.title")}
              </LinkButton>
            </div>
            <div className={styles.reqDetailsData}>
              {(isFetchingTransaction ||
                isFetchingCorrections ||
                isFetchingLinkedAdjustment) && <Loader isBlocking />}
              {(data?.transactionType === TransactionType.Delivery ||
                data?.transactionType === TransactionType.Receipt) && (
                <ProcessingStatusBanner transaction={data!} />
              )}
              <TransactionStatusInfo transaction={data!} />
              <InvoiceLink
                prefix={t("invoices.linkPrefix")}
                invoiceNumber={data?.invoiceNumber}
                className={styles.transactionDetailsInvoice}
              />
              <TransactionContentView
                transaction={data!}
                linkedAdjustment={linkedAdjustmentData}
                corrections={mappedCorrections}
                isPermitted={isPermitted}
                company={company}
                activitiesQueryKey={activitiesQueryKey}
                transactionQueryKey={transactionDetailsQueryKey}
                correctionsQueryKey={correctionsQueryKey}
                beforeCorrections={
                  (isClaimNumberVisible || isTrailerNumberVisible) ? (
                    <>
                      {isClaimNumberVisible && (
                        <ClaimNumberLabel currentClaimNumber={data!.claimNumber} />
                      )}
                      {isTrailerNumberVisible && (
                        <TrailerNumberLabel currentTrailerNumber={data!.trailerNumber} />
                      )}
                    </>
                  ) : undefined
                }
                correctionsExtraActions={
                  isInCorrectionPeriod ? (
                    <>
                      {isClaimNumberVisible && (
                        <ClaimNumberEditButton
                          transactionId={data!.transactionId}
                          currentClaimNumber={data!.claimNumber}
                        />
                      )}
                      {isTrailerNumberVisible && (
                        <TrailerNumberEditButton
                          transactionId={data!.transactionId}
                          currentTrailerNumber={data!.trailerNumber}
                        />
                      )}
                    </>
                  ) : undefined
                }
              />
              {(canCreateReceipt || canCancel) && (
                <div className={styles.actions}>
                  {canCreateReceipt && (
                    <Button
                      id="transaction-action-create-receipt-button"
                      disabled={isLoadingTransaction}
                      onClick={openCreateReceiptDialog}
                    >
                      {t("transactions.actions.createReceipt")}
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      id="transaction-action-cancel-button"
                      className={styles.cancelButton}
                      disabled={isInvalidateLoading || isLoadingTransaction}
                      onClick={openCancelDialog}
                    >
                      {t("transactions.actions.cancel")}
                    </Button>
                  )}
                  {isInvalidateLoading && <Loader isBlocking />}
                </div>
              )}
              {isCancelDialogRendered && (
                <Dialog
                  modal
                  title={getDialogTitle(t("transactions.actions.cancelDialogTitle"))}
                  isActive={isCancelDialogActive}
                  onClose={() => {
                    closeCancelDialog();
                    setCancelReason("");
                  }}
                  actions={[
                    <Button
                      key="confirm"
                      id="cancel-dialog-confirm-button"
                      className={styles.cancelButton}
                      disabled={isInvalidateLoading || !cancelReason.trim()}
                      onClick={() => applyInvalidate()}
                    >
                      {t("transactions.actions.cancelDialogTitle")}
                    </Button>,
                    <Button
                      key="close"
                      variant="outlined"
                      id="cancel-dialog-close-button"
                      onClick={() => {
                        closeCancelDialog();
                        setCancelReason("");
                      }}
                    >
                      {t("cancel")}
                    </Button>,
                  ]}
                >
                  {isInvalidateLoading && <Loader isBlocking />}
                  <TextInput
                    isMultiline
                    id="cancel-dialog-reason-input"
                    title={t("transactions.actions.cancelReason")}
                    value={cancelReason}
                    onChange={(e, value) => setCancelReason(value)}
                  />
                </Dialog>
              )}
              {isCreateReceiptDialogRendered && (
                <CreateReceiptDialog
                  transaction={data!}
                  isActive={isCreateReceiptDialogActive}
                  onClose={closeCreateReceiptDialog}
                  onSubmit={(values) => applyCreateReceipt(values)}
                  isLoading={isCreateReceiptLoading}
                />
              )}
            </div>
          </div>
        </main>
        {hasSidebar && (
          <CollapseComponent storageKey="sidebarCollapsed">
            <RecordsSidebar
              objectId={transactionNumber}
              entityType="transaction"
              entityName={t("transactions.details.entityName")}
              initiatorEmail={currentUser?.email || ""}
              getDialogTitle={getDialogTitle}
              fileNamePrefix={t("transactions.details.export.fileNamePrefix")}
              fileNameSuffix={transactionNumber}
              canViewHistory={canViewHistory}
              canExportHistory={canExportHistory}
              activitiesQueryKey={activitiesQueryKey}
              canViewForwardTraceability={
                canSeeLinkedBatches &&
                FORWARD_TRACE_TYPE_AVAILABLE_LIST.includes(transactionType)
              }
              canViewBackwardTraceability={
                canSeeLinkedBatches &&
                BACKWARD_TRACE_TYPE_AVAILABLE_LIST.includes(transactionType)
              }
              forwardTraceabilityQueryKey={forwardTraceabilityQueryKey}
              backwardTraceabilityQueryKey={backwardTraceabilityQueryKey}
              getBackwardTraceability={() =>
                api.getLinkedTransactions({
                  transactionNumber,
                  isTargetTransactionNext: false,
                })
              }
              getForwardTraceability={() =>
                api.getLinkedTransactions({
                  transactionNumber,
                  isTargetTransactionNext: true,
                })
              }
              transactionOrTransfer={data!}
            />
          </CollapseComponent>
        )}
        {isDispositionEditVisible && editDispositionContainer && (
          <>
            {isDialogRendered && (
              <EditDispositionDialog
                transaction={data!}
                onClose={closeDialog}
                isActive={isDialogActive}
                dialogTitle={getDialogTitle(
                  t("transactions.details.disposition.dialogTitle")
                )}
                activitiesQueryKey={activitiesQueryKey}
                transactionDetailsQueryKey={transactionDetailsQueryKey}
              />
            )}
            {createPortal(
              <SectionEditAction
                canEdit
                editCallback={openDialog}
                actionName="EditDisposition"
              />,
              editDispositionContainer
            )}
          </>
        )}
      </>
    );
  };

  const isLoading =
    isLoadingTransaction || isLoadingCorrections || isLoadingLinkedAdjustment;
  const isError = isErrorTransaction || isErrorCorrections;

  return (
    <div className={styles.reqDetails}>
      {isLoading && <Loader isBlocking />}
      {!isLoading && !isError && data && renderContent()}
      {isError && (
        <NotFoundPage
          returnLink="/transactions"
          title={
            isTransactionNotFound
              ? "transactions.details.error.notFoundTitle"
              : "errors.somethingWrong"
          }
          descriptions={["transactions.details.error.description"]}
          returnLinkDescription="transactions.details.error.returnLink"
        />
      )}
    </div>
  );
};

export default TransactionDetailsView;
